import { DataLoader } from '../../../core/types/data-loader';
import {
  StateStore,
  PartialStateStore
} from '../../../core/types/application-state';
import {
  isEmpty,
  isObject,
  withDefault,
  getCookie,
  setCookie,
  isEmptyObject
} from '../../../core/util';
import { getOCRCustomerDataById } from '../../shared/services/customer-service';
import {
  getRPSPlans,
  getRPSPlansBySSN,
  getRPSPlanInfo,
  updateRPSCustomerSSN
} from '../../shared/services/rps-plans-service';
import { getCustomerHouseholdDataById } from '../../shared/services/household-service';
import {
  loadFONASUCustomerData,
  commitFONASUPersonalInformation
} from '../shared/loaders';
import { houseHoldSummitData } from '../../shared/summit.utils';
import {
  consumerTypes,
  rolloverTypes,
  autoSplitPercents,
  updateRIWithholding,
  isRetirementIncomeFundPresent
} from '../../shared/utils';
import {
  rpsBeneficiaryTypes,
  isRPSOCRTraditionalAccount,
  isRPSOCROmniAccount,
  isMailingAddressDifferent
} from '../shared/utils';
import {
  formatBeneficiariesForApp,
  formatOCRPDFPayload,
  getTRACDistributionStatus,
  getTRACRolloverNoticeInput,
  getExistingFundAgreementId,
  getOCRInvestmentMethodType,
  getRPSOCRAgreementType,
  getPreAuthPaperlesWithDrawalInput,
  getTRACLoanBalanceCashDistributionInput,
  getHasLoanBalanceInput,
  getHasBrokerageHoldingsInput,
  getRMDAgeByYearEndInput,
  getTermedOrRetiredStatusInput,
  isPartialRolloverAmt,
  checkForOCRAltFlow,
  formatOCRBeneData,
  getDefaultRPSToAccountType,
  getExistingRPSToAccountType,
  defaultRPSOCRAccountTypeToAgreementType,
  isExistingFundsChosen
} from './utils';
import { getOCRExistingAgreementsService } from '../../shared/services/existing-agreements-service';

import { analyticsDataLayer } from '../../../analytics/analytics-helper';

import { formatDate } from '../../shared/dateUtils';
import { getRPSExistingBeneficiaries } from '../../shared/services/rps-beneficiaries-service';
import { getRPSOCRMaxRolloverAmountService } from '../../shared/services/rps-plans-ocr-rollover-amount-service';
import { createOCROMNIApplicationService } from '../../shared/services/ocr-omni-submission-service';
import { createOCRTRACApplicationService } from '../../shared/services/ocr-trac-submission-service';
import { createOCRPDFService } from '../../shared/services/ocr-pdf-service';

const bpId = getCookie('bpId');

export const loadRPSPlans = async (): Promise<PartialStateStore> => {
  try {
    const rpsPlans = await getRPSPlans(Number(bpId));
    const hasSSNInRPSPlans = Boolean(rpsPlans?.taxID);

    if (hasSSNInRPSPlans && rpsPlans?.planNumbers?.length > 0) {
      if (rpsPlans?.planNumbers?.length < 2) {
        return {
          loadedData: {
            rpsPlanNumber: rpsPlans?.planNumbers[0],
            taxID: rpsPlans?.taxID,
            taxIDType: 'SSN'
          }
        };
      }
      const res = {
        app: {},
        loadedData: {
          rpsPlans
        }
      };
      return res;
    }

    return {
      loadedData: {
        noSSNInRPSPlans: true
      }
    };
  } catch (err) {
    return {
      loadedData: {
        noSSNInRPSPlans: true
      }
    };
  }
};

export const updateRPSCustomerWithSSN = async (rpsSSN): Promise<any> => {
  try {
    await updateRPSCustomerSSN(Number(bpId), rpsSSN);
    return {
      updateRPSCustomerSSNFailed: false
    };
  } catch (err) {
    return {
      updateRPSCustomerSSNFailed: true
    };
  }
};

export const commitRPSPlansInfoBySSN = async (inputs): Promise<any> => {
  const { rpsSSN } = inputs;
  const formattedSSN = rpsSSN?.replace(/[^*0-9]/g, '');

  try {
    const rpsPlans = await getRPSPlansBySSN(formattedSSN);
    if (rpsPlans?.planNumbers?.length === 0) {
      return {
        loadedData: {
          noRPSPlansFound: true
        }
      };
    }
    const { updateRPSCustomerSSNFailed } = await updateRPSCustomerWithSSN(
      rpsSSN
    );

    if (rpsPlans?.planNumbers?.length < 2) {
      return {
        loadedData: {
          rpsPlanNumber: rpsPlans?.planNumbers[0],
          taxID: rpsPlans?.taxID,
          taxIDType: 'SSN',
          noSSNInRPSPlans: false,
          updateRPSCustomerSSNFailed
        }
      };
    }
    const res = {
      app: {},
      loadedData: {
        ...(!updateRPSCustomerSSNFailed && { rpsPlans }),
        noSSNInRPSPlans: false,
        updateRPSCustomerSSNFailed
      }
    };
    return res;
  } catch (err) {
    return {
      loadedData: {
        noRPSPlansFound: true
      }
    };
  }
};

export const commitRPSPlans: DataLoader = async ({
  store,
  chosen,
  inputs
}): Promise<any> => {
  if (isEmpty(chosen) || chosen === 'landing') {
    return {
      loadedData: {
        ...store?.loadedData,
        inOCRAltFlow: checkForOCRAltFlow(window.location.search)
      }
    };
  }
  const { section } = chosen;

  if (section === 'rpsSSNModal') {
    return commitRPSPlansInfoBySSN(inputs);
  }

  const { rpsPlanNumber } = chosen;

  const { taxID = '', taxIDType = '' } = store?.loadedData?.rpsPlans || {};

  return {
    loadedData: {
      rpsPlans: [],
      rpsPlanNumber,
      taxID,
      taxIDType
    }
  };
};

export const makeLoadOCROMNIData: DataLoader = async (
  { store },
  flowProps
): Promise<StateStore> => {
  if (!store?.loadedData?.rpsPlanNumber) {
    const rpsPlansData = await loadRPSPlans();
    const {
      loadedData: { taxID, taxIDType, rpsPlanNumber, rpsPlans, noSSNInRPSPlans }
    } = rpsPlansData;

    return {
      app: {},
      accountType: flowProps?.accountType,
      loadedData: {
        taxID,
        taxIDType,
        rpsPlans,
        rpsPlanNumber,
        noSSNInRPSPlans,
        previousRolloverAmt: getCookie('rolloverAmt')
      }
    };
  }

  return {
    ...store
  };
};

export const loadOCRRolloverAmount: DataLoader = async ({
  store
}): Promise<PartialStateStore> => {
  const ssn = store?.loadedData?.taxID;
  const planNumber = store?.loadedData?.rpsPlanNumber;
  const agreementType:
    | string
    | undefined = defaultRPSOCRAccountTypeToAgreementType(store?.accountType);

  const ocrAmountInfo = await getRPSOCRMaxRolloverAmountService(
    ssn,
    planNumber
  );

  const error = '';

  const hasRolloverAmount = Boolean(ocrAmountInfo?.maxRolloverAmount);
  const maxRolloverAmt = hasRolloverAmount
    ? ocrAmountInfo?.maxRolloverAmount
    : '0';

  // US682541: [Compliance] OCR - Apply Existing Benes
  const beneficiariesData = await getRPSExistingBeneficiaries(
    Number(bpId),
    ssn,
    planNumber,
    agreementType
  );
  const formattedOCRBeneData = formatOCRBeneData(beneficiariesData);

  return {
    loadedData: {
      ...formattedOCRBeneData,
      error,
      maxRolloverAmt,
      updatedExistingAgreements: []
    }
  };
};

export const loadOCRExistingAgreementsAndCustomerData: DataLoader = async ({
  store
}): Promise<PartialStateStore> => {
  const { taxID = '', rpsPlanNumber = '' } = store?.loadedData || {};
  const agreementType = getRPSOCRAgreementType(store?.accountType);
  const [
    customerData,
    existingAgreements,
    existingRPSPlanInfo,
    householdData
  ] = await Promise.all([
    getOCRCustomerDataById(taxID, rpsPlanNumber),
    getOCRExistingAgreementsService(Number(bpId), agreementType),
    getRPSPlanInfo(taxID, rpsPlanNumber),
    getCustomerHouseholdDataById(Number(bpId))
  ]);

  const summitData = houseHoldSummitData(householdData);

  const customerDataConverted = loadFONASUCustomerData(customerData);

  const mailingAddressDifferent = isMailingAddressDifferent(
    customerDataConverted
  );

  const existingRPSAgreements = withDefault(
    existingRPSPlanInfo?.fundsInPlan
      ?.filter(({ trpFund }) => trpFund === true)
      .map(({ retailFundInfo }) => ({
        name: retailFundInfo?.shortName,
        ticker: retailFundInfo?.ticker
      })),
    []
  );

  const existingIRAAgreements = isObject(existingAgreements)
    ? []
    : existingAgreements;

  return {
    app: {
      customer: customerDataConverted,
      newInvestments: [],
      retirementPlanInfo: {
        planKey: {
          planNumber: rpsPlanNumber
        },
        planName: existingRPSPlanInfo?.planName
      }
    },
    rolloverType: rolloverTypes.DIRECT,
    mailingAddressDifferent,
    loadedData: {
      infoValid: false,
      beneInfoEditMode: false,
      beneInfoValid: false,
      portfolioValid: false,
      deliveryMethodValid: false,
      paperlessConsentValid: false,
      customerInfoEditMode: true,
      portfolioEditMode: false,
      existingAgreementsEditMode: false,
      existingAgreementsValid: false,
      paperlessEditMode: false,
      deliveryEditMode: false,
      fundingType: '',
      associateLoginID: '',
      primaryBeneficiaryInfo: false,
      secondaryBeneficiaryInfo: false,
      rolloverAmountOption: '',
      termStatus: withDefault(customerData?.terminated, false),
      existingAgreements: existingIRAAgreements,
      userHaveExistingAgreements: withDefault(
        existingIRAAgreements?.length > 0,
        false
      ),
      existingRPSAgreements,
      noSSNInRPSPlans: false,
      iiBeneDataPresent: false,
      rpsBeneDataPresent: false
    },
    summitData,
    consumerName: consumerTypes.FONASU
  };
};

export const commitRPSOCRDeliveryMethod = async (
  chosen
): Promise<PartialStateStore> => {
  const { deliveryType } = chosen;

  const res = {
    loadedData: {
      ocrDeliveryMethod: deliveryType,
      deliveryEditMode: false,
      deliveryMethodValid: true
    }
  };
  return res;
};

export const commitPaperlessConsent = async (
  chosen
): Promise<PartialStateStore> => {
  const { paperlessConsent } = chosen;

  const res = {
    loadedData: {
      paperlessDelivery: paperlessConsent,
      paperlessEditMode: false,
      paperlessConsentValid: true,
      deliveryEditMode: true
    }
  };
  return res;
};

export const commitRPSOCRUpdateExistingAgreements = async (
  chosen,
  store
): Promise<PartialStateStore> => {
  const { updatedExistingAgreements, existingAgreementType } = chosen;
  const {
    app: { newInvestments },
    amount
  } = store;

  const ssn = store?.loadedData?.taxID;
  const planNumber = store?.loadedData?.rpsPlanNumber;

  // combine existing funds with selected new funds to make sure we are
  // not dropping new funds when existing funds section is edited
  const allSelectedFunds = [...updatedExistingAgreements, ...newInvestments];
  // formats selected(existing + new) funds to display them in the portfolio cart
  const formatSelectedExistingFunds = autoSplitPercents(
    allSelectedFunds,
    amount
  );

  const existingAgreementPresent = isExistingFundsChosen(
    formatSelectedExistingFunds,
    existingAgreementType
  );
  let formattedOCRBeneData: Array<any> = [];

  if (existingAgreementPresent) {
    // load beneficary data for selected existing account type
    const beneficiariesData = await getRPSExistingBeneficiaries(
      Number(bpId),
      ssn,
      planNumber,
      existingAgreementType
    );
    formattedOCRBeneData = formatOCRBeneData(beneficiariesData);
  }

  return {
    amount,
    loadedData: {
      // unless bene data is updated, do not override existing bene data
      ...(!isEmptyObject(formattedOCRBeneData) && { ...formattedOCRBeneData }),
      selectedFunds: formatSelectedExistingFunds,
      updatedExistingAgreements,
      existingAgreementsEditMode: false,
      existingAgreementsValid: true,
      portfolioEditMode: true,
      existingAgreementType
    }
  };
};

export const commitRPSOCRNewInvestments = async (
  chosen,
  store
): Promise<PartialStateStore> => {
  const {
    funds,
    fundingType,
    rolloverAmt,
    rolloverAmountOption,
    matchingAgreementArray
  } = chosen;
  const {
    app: { customer },
    loadedData
  } = store;

  let arrNewFundTickers: any = [];
  let newFundPresent = false;
  let existingFundsPresent = false;
  const existingAgreements = loadedData?.existingAgreements;
  // Get Tickers from New Funds
  const fundsTickers = funds?.map(({ ticker }) => ticker);

  // Get Tickers from Existing Agreements
  const existingFundsTickers = existingAgreements?.map(({ ticker }) => ticker);

  // filter out non matching tickers
  arrNewFundTickers = fundsTickers?.filter(
    arrVal => !existingFundsTickers?.includes(arrVal)
  );

  // check length of non-matching ticker array
  if (arrNewFundTickers?.length >= 1) {
    newFundPresent = true;
  }

  // Get name, trpCode, ticker and percent from chosen fund details
  const chosenFundsDetails = funds?.map(
    ({ name, ticker, trpCode, percent }) => ({
      name,
      trpCode,
      ticker,
      percent
    })
  );

  const newFundsName = chosenFundsDetails
    ?.filter(({ ticker }) => arrNewFundTickers.includes(ticker))
    .map(({ name }) => ({ name }));

  const newFunds = funds
    ?.map(fund => fund)
    .filter(({ ticker }) => arrNewFundTickers.includes(ticker));

  const hasRetirementIncomeFund = isRetirementIncomeFundPresent(funds);

  // get new funds from the funds chosen
  const newInvestments = newFunds?.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  // get existing funds from the funds chosen
  const existingFunds = funds
    ?.map(fund => fund)
    .filter(({ ticker }) => !arrNewFundTickers.includes(ticker));

  // check length of exisitng funds array
  if (existingFunds?.length >= 1) {
    existingFundsPresent = true;
  }

  const existingAccounts = existingFunds?.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      investmentVehicle: {
        ticker,
        trpCode
      },
      // gets the agreementID using the existing agreements array
      // fetched from the agreements service in two scenarios:
      // (1) When user selects an existing fund from existing funds section
      // (2) When user removes the existing fund selected in (1) and adds it again
      // via fund quick search
      agreementID: getExistingFundAgreementId(
        ticker,
        existingAgreements,
        loadedData?.updatedExistingAgreements
      ),
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );
  const hasExistingIRA = existingAccounts?.length > 0;

  // formats selected funds(existing + new) to display them in the portfolio cart
  const formatSelectedFunds = funds?.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      customer: {
        ...customer,
        businessPartyId: Number(bpId)
      },
      investmentMethod: getOCRInvestmentMethodType(
        existingFundsPresent,
        newFundPresent
      ),
      newInvestments,
      existingAccounts
    },
    chosenInvestments: chosen?.funds,
    amount: rolloverAmt,
    loadedData: {
      selectedFunds: formatSelectedFunds,
      portfolioValid: true,
      ...(!newFundPresent && {
        portfolioEditMode: false,
        beneInfoEditMode: true
      }),
      hasExistingIRA,
      fundingType,
      rolloverAmountOption,
      newFundPresent,
      scrollableModal: newFundPresent,
      newFundsName,
      chosenFundsDetails,
      matchingAgreementArray,
      hasRetirementIncomeFund
    }
  };
};

const commitRPSOCRBeneficairyInfo = async (inputs): Promise<any> => {
  const {
    primaryBeneficiaryInfo,
    secondaryBeneficiaryInfo,
    primaryBenes,
    secondaryBenes
  } = inputs;

  const res = {
    loadedData: {
      beneInfoValid: true,
      beneInfoEditMode: false,
      paperlessEditMode: true,
      primaryBeneficiaryInfo,
      primaryBeneficiaries: (primaryBenes || []).map(person => ({
        beneType: rpsBeneficiaryTypes.PRIMARY,
        name: {
          firstName: person.primaryBeneFirstName,
          middleName: person.primaryBeneMiddleName,
          lastName: person.primaryBeneLastName
        },
        dateOfBirth: person.primaryBeneDob
          ? formatDate(person.primaryBeneDob, 'MM/DD/YYYY', '')
          : '',
        taxID: withDefault(person.primaryBeneSsn?.replace(/[^*0-9]/g, ''), ''),
        relationship: person.primaryBeneRelationship,
        percentageDistribution: person.primaryBenePercentDistribution,
        beneCategoryType: person.primaryBeneCategoryType,
        entityName: person.primaryBeneEntityName
      })),
      secondaryBeneficiaryInfo,
      secondaryBeneficiaries: (secondaryBenes || []).map(person => ({
        beneType: rpsBeneficiaryTypes.SECONDARY,
        name: {
          firstName: person.secondaryBeneFirstName,
          middleName: person.secondaryBeneMiddleName,
          lastName: person.secondaryBeneLastName
        },
        dateOfBirth: person.secondaryBeneDob
          ? formatDate(person.secondaryBeneDob, 'MM/DD/YYYY', '')
          : '',
        taxID: withDefault(
          person.secondaryBeneSsn?.replace(/[^*0-9]/g, ''),
          ''
        ),
        relationship: person.secondaryBeneRelationship,
        percentageDistribution: person.secondaryBenePercentDistribution,
        beneCategoryType: person.secondaryBeneCategoryType,
        entityName: person.secondaryBeneEntityName
      }))
    }
  };
  return res;
};

export const commitRPSOCRSection = async (
  chosen,
  inputs,
  store
): Promise<any> => {
  const { section } = chosen;

  if (section === 'updateExistingAgreements') {
    return commitRPSOCRUpdateExistingAgreements(chosen, store);
  }
  if (section === 'ocrBeneficariesInfo') {
    return commitRPSOCRBeneficairyInfo(inputs);
  }
  if (section === 'ocrDeliveryMethod') {
    return commitRPSOCRDeliveryMethod(chosen);
  }
  if (section === 'ocrPaperlessConsent') {
    return commitPaperlessConsent(chosen);
  }
  return commitRPSOCRNewInvestments(chosen, store);
};

/* eslint-disable complexity */
export const createRPSOCRApplication: DataLoader = async ({
  store,
  inputs,
  chosen
}): Promise<PartialStateStore> => {
  const { app, accountType, chosenInvestments } = store;

  if (isEmpty(chosen) || chosen?.section === 'addressValidation') {
    return commitFONASUPersonalInformation(inputs, store, chosen);
  }

  if (chosen.section) {
    return commitRPSOCRSection(chosen, inputs, store);
  }

  if (chosen === 'rpsNewFundsModal') {
    return {
      loadedData: {
        portfolioEditMode: false,
        beneInfoEditMode: true
      }
    };
  }

  if (chosen === 'ocrSubmit') {
    const {
      primaryBeneficiaries = [],
      secondaryBeneficiaries = [],
      hasExistingIRA = false,
      existingAgreementType = '',
      hasRetirementIncomeFund = false
    } = store?.loadedData || {};
    const isOCRTraditionalAcc = isRPSOCRTraditionalAccount(accountType);
    const isRPSOMNI = isRPSOCROmniAccount(accountType);

    // check for Retirement Income and tax withholding
    const { taxWithholdingOption, taxWithholdingPercent = 10 } = inputs;
    const finalizedNewInvestments = hasRetirementIncomeFund
      ? updateRIWithholding(
          app.newInvestments,
          taxWithholdingOption,
          taxWithholdingPercent,
          chosenInvestments
        )
      : app.newInvestments;

    const updatedAppState = {
      ...app,
      estimatedRolloverAmount: store?.amount,
      associateLoginID: store?.loadedData?.associateLoginID,
      rolloverType: store?.rolloverType,
      toAccountType: hasExistingIRA
        ? getExistingRPSToAccountType(existingAgreementType)
        : getDefaultRPSToAccountType(isOCRTraditionalAcc),
      beneficiaries: formatBeneficiariesForApp(
        primaryBeneficiaries,
        secondaryBeneficiaries
      ),
      deliveryType: store?.loadedData?.ocrDeliveryMethod,
      ocrData: {
        iseDeliveryUpdateNeeded: withDefault(
          store?.loadedData?.paperlessDelivery,
          false
        ),
        hasExistingIRA: store?.loadedData?.hasExistingIRA
      },
      newInvestments: finalizedNewInvestments,
      ...(!isRPSOMNI && {
        tracOCRData: {
          preAuthPapperlessWithdrawal: getPreAuthPaperlesWithDrawalInput(
            inputs
          ),
          termedOrRetiredStatus: getTermedOrRetiredStatusInput(inputs),
          rmdageByYearEnd: getRMDAgeByYearEndInput(inputs),
          hasBrokerageHoldings: getHasBrokerageHoldingsInput(inputs),
          hasLoanBalance: getHasLoanBalanceInput(inputs),
          loanBalanceCashDistribution: getTRACLoanBalanceCashDistributionInput(
            inputs
          ),
          performDistribution: getTRACDistributionStatus(inputs),
          readRolloverOptionsNotice: getTRACRolloverNoticeInput(inputs)
        }
      }),
      amountType: isPartialRolloverAmt(store) ? 'PARTIAL' : 'FULL'
    };

    try {
      const response = isRPSOMNI
        ? await createOCROMNIApplicationService(updatedAppState)
        : await createOCRTRACApplicationService(updatedAppState);
      setCookie('rolloverAmt', store?.amount, '', true);
      return {
        loadedData: response
      };
    } catch (err) {
      return {
        loadedData: {
          ocrSubmissionError: true
        }
      };
    }
  }
  if (chosen === 'unlockMyInfoSection') {
    return {
      loadedData: {
        customerInfoEditMode: true,
        existingAgreementsEditMode: false,
        portfolioEditMode: false,
        beneInfoEditMode: false,
        deliveryEditMode: false,
        paperlessEditMode: false
      }
    };
  }

  if (chosen === 'unlockRPSExistingFundsSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        existingAgreementsEditMode: true,
        portfolioEditMode: false,
        beneInfoEditMode: false,
        deliveryEditMode: false,
        paperlessEditMode: false
      }
    };
  }

  if (chosen === 'unlockRPSChooseFundsSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        existingAgreementsEditMode: false,
        portfolioEditMode: true,
        beneInfoEditMode: false,
        deliveryEditMode: false,
        paperlessEditMode: false
      }
    };
  }

  if (chosen === 'unlockRPSBeneSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        existingAgreementsEditMode: false,
        portfolioEditMode: false,
        beneInfoEditMode: true,
        deliveryEditMode: false,
        paperlessEditMode: false
      }
    };
  }

  if (chosen === 'unlockRPSDeliverySection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        existingAgreementsEditMode: false,
        portfolioEditMode: false,
        beneInfoEditMode: false,
        deliveryEditMode: true,
        paperlessEditMode: false
      }
    };
  }

  if (chosen === 'unlockRPSPaperlessSection') {
    return {
      loadedData: {
        customerInfoEditMode: false,
        existingAgreementsEditMode: false,
        portfolioEditMode: false,
        beneInfoEditMode: false,
        deliveryEditMode: false,
        paperlessEditMode: true
      }
    };
  }

  return { app };
};
/* eslint-enable complexity */

export const createOCRPDF: DataLoader = async ({
  store,
  chosen
}): Promise<PartialStateStore> => {
  if (chosen === 'ocrPDF') {
    try {
      const ocrPDFPayload = formatOCRPDFPayload(store);
      const loadedData = await createOCRPDFService(ocrPDFPayload);
      if (loadedData?.success) {
        return {
          loadedData
        };
      }

      return {
        loadedData: {
          ...loadedData,
          shouldGenerateOCRPDF: false
        }
      };
    } catch (err) {
      return {
        loadedData: {
          shouldGenerateOCRPDF: false
        }
      };
    }
  }

  return store;
};
